import moment from 'moment';
import { useEffect, type CSSProperties } from 'react';
import { useDispatch } from 'react-redux';

import { FETCH_JOB_ACTION, fetchJob } from '../../../actions';
import { createLoadingSelector } from '../../../api/selectors';
import { Column, DataRow, Screen, Tooltip } from '@tempmee/react';
import { BackButton, CenteredSpinner, AmountRow, EdtBadge } from '~/commonComponents';
import { PROMO_ROW_LABEL } from '~/constants/promo';
import { useAppSelector } from '~/hooks';
import { ITransaction } from '~/models';
import { Links } from '../../../constants';
import { showCandidateOvertime } from '../../../growthbook';
import roundedPattern from '../../../images/rounded_pattern.png';
import { SolidInformationCircleIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Colors } from '../../../themes/colors';
import { isEmployeeOption } from '../../WorkerClassification/utils';
import { getJobStatus, returnTransactionComponent } from './utils';
import { UserSubProfession } from '../../../enums/UserProfession';
import { useParams } from 'react-router';
import { routes } from '~/routes';
import { currencyFormat } from '~/utils/currencyFormat';

const adjustmentMadeTooltip = (
  <>
    If “Yes” is indicated in this field, there has been an adjustment to the hours of the shift that
    resulted on an additional charge or a refund to the original total paid. <br />
    <br /> If “No” is indicated in this field, there has not been an adjustment made that impacts
    the initial payment amount.
  </>
);

const convenienceFeeTooltip = (
  <>
    GoTu charges a 3.5% convenience fee on Credit Card transactions. If you do not wish to be
    charged the convenience fee, please update your billing to E-check/ACH.
  </>
);

const overtimeTooltip =
  'Overtime pay (1.5x rate) is required for hours that exceed the state’s daily and/or weekly regular working hour limits.';

export default function TransactionsInfo() {
  const params = useParams<{ jobId: string }>();
  const dispatch = useDispatch();

  const user = useAppSelector((state) => state.user.user);
  const job = useAppSelector((state) => state.job.job);

  const loadingSelector = createLoadingSelector([FETCH_JOB_ACTION]);
  const isLoading = useAppSelector((state) => loadingSelector(state));

  const renderCancelationInfo = () => (
    <div
      style={{
        padding: '15px 10px',
        backgroundColor: Colors.neutral_50,
        marginBottom: 20,
      }}
    >
      <DataRow divider={false}
        label="Cancellation Date:"
        value={moment(job?.job_cancelled_datetime).format('ddd, MMM DD, YYYY')}
      />
      <DataRow divider={false}
        label="Cancellation Time:"
        value={moment(job?.job_cancelled_datetime).format('hh:mm a')}
      />
      {job?.cancelPerformedBy && (
        <DataRow divider={false}
          label="Cancelled by:"
          value={`${job?.cancelPerformedBy?.first_name || ''} ${
            job?.cancelPerformedBy?.last_name || ''
          }`}
        />
      )}
    </div>
  );

  useEffect(() => {
    if (!job || job?.id !== params?.jobId) {
      dispatch(fetchJob({ jobId: params?.jobId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!job || isLoading) {
    return (
      <Screen variant="standard">
        <Screen.Header start={<BackButton url={routes.paymentHistory} />} title="Transaction History" />
        <Screen.Body style={{ alignItems: 'center' }}>
          <CenteredSpinner minHeight={400} />
        </Screen.Body>
      </Screen>
    );
  }

  const isVoided = job?.status === 'cancelled' || job?.status === 'expired';

  return (
    <Screen variant="standard">
      <Screen.Header start={<BackButton url={routes.paymentHistory} />} title="Transaction History" />
      <Screen.Body style={{ alignItems: 'center' }}>
        <p
          className="global_font"
          style={{
            margin: 0,
            marginBottom: 28,
            fontSize: 18,
            fontWeight: 'bold',
            color: colors.neutral[600],
          }}
        >
          {job?.isOnsiteInterview ? 'Interview' : 'Shift'} Status:{' '}
          <span
            style={{
              fontWeight: 'normal',
              color: getJobStatus(job?.status)?.color,
            }}
          >
            {getJobStatus(job?.status)?.label || job?.status}
          </span>
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '80%',
            padding: '35px 5%',
            marginBottom: 16,
            boxShadow: '0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
          }}
        >
          <p
            className="global_font"
            style={{
              margin: 0,
              marginBottom: 22,
              fontSize: 20,
              fontWeight: 'bold',
              color: colors.neutral[600],
              textAlign: 'center',
            }}
          >
            {job?.is_cancelled ? 'Original' : 'Final'}{' '}
            {job?.isOnsiteInterview ? 'Interview' : 'Shift'} Details
          </p>

          <div
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Column gap={1} width="45%">
              <DataRow divider={false} label="Date:" value={job?.local_date} />
              {job.offer_final_reciever && (
                <DataRow divider={false}
                  label="Professional:"
                  // eslint-disable-next-line max-len
                  value={`${job.offer_final_reciever?.first_name} ${job.offer_final_reciever?.last_name}`}
                />
              )}
              <DataRow divider={false}
                label="Profession:"
                value={UserSubProfession.getName(job?.subProfession) ?? job?.professionName}
              />
              <DataRow divider={false}
                label="Preferred Specialty:"
                value={job?.specialty?.split('_')?.[1] || 'General'}
              />
              <DataRow divider={false} label="Scheduled By:" value={job?.offer_owner?.full_name} />
            </Column>

            <Column gap={1} width="45%">
              <DataRow divider={false}
                label="Hours:"
                icon={<EdtBadge>{job?.local_timezone}</EdtBadge>}
                value={`${job?.local_start_time} - ${job?.local_end_time}`}
              />
              <AmountRow label="Hourly Rate:" amount={job?.rate} />
              <DataRow divider={false}
                label="Meal Break:"
                value={`${job?.lunch_break_time || 0} min ${
                  job?.is_lunch_break_paid ? '(Paid)' : '(Unpaid)'
                }`}
              />
              <DataRow divider={false}
                label="Adjustment Made:"
                icon={
                  <Tooltip title={adjustmentMadeTooltip}>
                    <SolidInformationCircleIcon color={colors.neutral[300]} />
                  </Tooltip>
                }
                value={job?.isAdjusted ? 'Yes' : 'No'}
              />
            </Column>
          </div>

          <div
            style={{
              width: '100%',
              borderBottom: '1px dashed #CBD4D8',
              marginTop: 35,
              marginBottom: 18,
            }}
          />

          <div
            key={job?.id}
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ width: '45%' }}>
              {!showCandidateOvertime() ? (
                <AmountRow
                  label={`Professional Pay (${job?.workingHourls?.toFixed(1)} hrs):`}
                  amount={job?.amount}
                  struck={isVoided}
                />
              ) : (
                <>
                  {job?.regularAndOvertimeHours?.regular > 0 && job?.totalRegular > 0 && (
                    <AmountRow
                      label={`Regular Rate Pay (${job?.regularAndOvertimeHours?.regular?.toFixed(
                        2,
                      )} hrs):`}
                      amount={job?.totalRegular}
                      struck={isVoided}
                    />
                  )}
                  {job?.regularAndOvertimeHours?.overtime > 0 && job?.totalOvertime > 0 && (
                    <AmountRow
                      label={`Overtime Pay (${job?.regularAndOvertimeHours?.overtime?.toFixed(
                        2,
                      )} hrs):`}
                      icon={
                        <Tooltip title={overtimeTooltip}>
                          <SolidInformationCircleIcon color={colors.neutral[300]} />
                        </Tooltip>
                      }
                      amount={job?.totalOvertime}
                      struck={isVoided}
                    />
                  )}
                </>
              )}

              <AmountRow
                label={`Service Fee (${job?.tm_fee_percent}%):`}
                amount={job?.tm_fee}
                struck={isVoided}
              />
              {isEmployeeOption(user) && (
                <AmountRow
                  label="Employee Fee:"
                  amount={job?.employeeOptionFee}
                  struck={isVoided}
                />
              )}
              {job?.convenience_fee ? (
                <AmountRow
                  label={`Convenience Fee (${job?.convenience_fee_percent?.toFixed(1)}%):`}
                  icon={
                    <Tooltip title={convenienceFeeTooltip}>
                      <SolidInformationCircleIcon color={colors.neutral[300]} />
                    </Tooltip>
                  }
                  amount={job?.convenience_fee}
                  struck={isVoided}
                />
              ) : (
                <></>
              )}

              {job?.counterCover ? (
                <AmountRow
                  label="GoTu Counter Cover:"
                  amount={job.counterCover.amount}
                  variant="credit"
                />
              ) : null}

              {job?.promoDiscount?.amount ? (
                <AmountRow
                  label={`${PROMO_ROW_LABEL}:`}
                  amount={job.promoDiscount.amount}
                  variant="credit"
                />
              ) : null}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                width: '45%',
              }}
            >
              {job?.is_cancelled && renderCancelationInfo()}

              <p
                className="global_font"
                style={{
                  margin: 0,
                  fontSize: 24,
                  fontWeight: 'bold',
                  color: colors.neutral[600],
                  width: '100%',
                  textAlign: 'right',
                }}
              >
                {job?.status === 'cancelled' ? 'Cancellation Fee: ' : 'Total Pay: '}
                <span style={{ color: colors.success[500] }}>
                  {`$${currencyFormat(job?.totalTransactionAmount)}`}
                </span>
              </p>
            </div>
          </div>
        </div>

        {job?.is_cancelled && (
          <p
            className="global_font"
            style={{
              color: colors.neutral[600],
              fontSize: 15,
              margin: 0,
              marginBottom: 12,
            }}
          >
            For full details on the Offices Cancellation Policy, please see{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={Links.termsOfUse}
              style={{ color: colors.primary[500], fontWeight: 'bold', textDecoration: 'none' }}
            >
              Terms and Conditions.
            </a>
          </p>
        )}

        {job?.counterCover && (
          <span
            style={{
              fontFamily: 'Nunito',
              fontSize: 15,
              color: colors.neutral[600],
              textAlign: 'center',
              marginTop: 8,
              marginBottom: 8,
            }}
          >
            The Total Pay shown above reflects that GoTu’s Counter Cover was applied successfully.
          </span>
        )}

        <div style={{ position: 'relative', width: '90%', marginTop: 16 }}>
          <div
            style={{
              backgroundColor: Colors.neutral_70,
              padding: '50px 30px',
              boxShadow: '0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
            }}
          >
            <p
              className="global_font"
              style={{
                margin: 0,
                marginBottom: 30,
                fontSize: 20,
                fontWeight: 'bold',
                color: colors.neutral[600],
                textAlign: 'center',
              }}
            >
              Transaction Table
            </p>

            <div style={{ display: 'flex', marginBottom: 15 }}>
              <p style={Styles.columnTitle}>Charge Date</p>
              {!showCandidateOvertime() ? (
                <p style={Styles.columnTitle}>Professional Pay</p>
              ) : (
                <p style={Styles.columnTitle}>Total Professional Pay</p>
              )}
              <p style={Styles.columnTitle}>Service Fee</p>
              {isEmployeeOption(user) && <p style={Styles.columnTitle}>Employee Fee</p>}
              <p style={Styles.columnTitle}>Convenience Fee</p>
              <p style={Styles.columnTitle}>Total</p>
              <p style={Styles.columnTitle}>Status</p>
            </div>

            {job?.transactions?.map((transaction: ITransaction) => {
              const { hygienistPay, serviceFee, convenienceFeeWrongName, employeeOptionFee } =
                returnTransactionComponent(transaction);

              return (
                <div style={{ display: 'flex' }} key={transaction?.transaction_id}>
                  <p style={Styles.columnItem}>{moment(transaction?.date).format('MM/DD/YYYY')}</p>

                  <p style={Styles.columnItem}>
                    {hygienistPay
                      ? `${transaction?.type === 'debit' ? '' : '-'}$${currencyFormat(hygienistPay)}`
                      : '-'}
                  </p>

                  <p style={Styles.columnItem}>
                    {serviceFee
                      ? `${transaction?.type === 'debit' ? '' : '-'}$${currencyFormat(serviceFee)}`
                      : '-'}
                  </p>

                  {isEmployeeOption(user) && job.employeeOptionFee && (
                    <p style={Styles.columnItem}>
                      {employeeOptionFee
                        ? `${transaction?.type === 'debit' ? '' : '-'}$${currencyFormat(
                            employeeOptionFee,
                          )}`
                        : '-'}
                    </p>
                  )}

                  <p style={Styles.columnItem}>
                    {convenienceFeeWrongName
                      ? `${
                          transaction?.type === 'debit' ? '' : '-'
                        }$${currencyFormat(convenienceFeeWrongName)}`
                      : '-'}
                  </p>

                  <p style={Styles.columnTitle}>
                    {`${transaction?.type === 'debit' ? '' : '-'}$${currencyFormat(
                      transaction?.amount,
                    )}`}
                  </p>

                  <p
                    className="global_font"
                    style={{
                      margin: 0,
                      textAlign: 'center',
                      fontSize: 16,
                      fontWeight: 'bold',
                      width: 'calc(100% / 6)',
                      color:
                        transaction?.type === 'debit' ? colors.success[500] : colors.primary[500],
                    }}
                  >
                    {transaction?.type === 'debit' ? 'CHARGED' : 'REFUNDED'}
                  </p>
                </div>
              );
            })}

            <div
              style={{
                width: '100%',
                borderBottom: '1px solid #819096',
                marginTop: 20,
                marginBottom: 18,
              }}
            />

            <div style={{ display: 'flex' }}>
              <div style={{ width: 'calc(100% / 2)' }} />

              <p style={Styles.columnTitle}>Total Amount</p>
              <p style={Styles.columnTitle}>
                {job?.totalTransactionAmount < 0
                  ? `-$${currencyFormat(job?.totalTransactionAmount * -1)}`
                  : `$${currencyFormat(job?.totalTransactionAmount)}`}
              </p>
            </div>
          </div>

          <img
            src={roundedPattern}
            style={{
              position: 'absolute',
              backgroundColor: 'white',
              top: '89%',
              objectFit: 'cover',
              width: '100%',
            }}
            alt="rounded_pattern"
          />
        </div>
      </Screen.Body>
    </Screen>
  );
}

const Styles: Record<string, CSSProperties> = {
  columnTitle: {
    fontFamily: 'Nunito',
    fontWeight: 'bold',
    width: 'calc(100% / 6)',
    margin: 0,
    textAlign: 'center',
    color: colors.neutral[600],
  },
  columnItem: {
    fontFamily: 'Nunito',
    width: 'calc(100% / 6)',
    margin: 0,
    textAlign: 'center',
    color: colors.neutral[600],
  },
};
